import type { ISketchObject } from '../../../mesh/sketch/ISketchObject';
import type { SketchRelationDefinition } from './relationDefinition/SketchRelationDefinition';
import type {SketchRelationSnapshot} from '../../../../types/sketch/solver/constraint/SketchRelationContainer';

/**
 * 判断对象是否为草图关系容器。
 *
 * @param value 待判断对象。
 * @returns 对象是否具有草图关系容器标记。
 */
export function isSketchRelationContainer(value: unknown): value is SketchRelationContainer {
	if ((typeof value !== 'object' && typeof value !== 'function') || value === null) {
		return false;
	}

	// 使用稳定标记避免跨包运行时的 `instanceof` 失效。
	return Reflect.get(value, 'isSketchRelationContainer') === true;
}

/**
 * 草图关系领域容器。
 *
 * 容器只维护关系定义、目标绑定和面板快照，不包含求解入口；统一参数协调器从草图
 * 运行时收集这些关系后编译方程。
 */
export class SketchRelationContainer<
	TOwner extends ISketchObject = ISketchObject,
> {
	/** 跨包判断草图关系容器的稳定运行时标记。 */
	public readonly isSketchRelationContainer = true;
	/** 容器所属草图对象。 */
	private readonly owner: TOwner;
	/** 以当前会话关系标识索引的关系定义。 */
	private readonly relationMap = new Map<number, SketchRelationDefinition<TOwner>>();
	/** 按派生领域来源保存的只读关系视图。 */
	private readonly derivedRelationSnapshotsBySource = new Map<string, readonly SketchRelationSnapshot[]>();

	/** @param owner 容器所属草图对象。 */
	public constructor(owner: TOwner) {
		this.owner = owner;
	}

	/**
	 * 检查是否存在指定领域类型的关系。
	 *
	 * @param type 关系领域类型。
	 * @returns 是否存在匹配关系。
	 */
	public hasType(type: unknown): boolean {
		return this.getRelations().some((relation) => relation.relationType === type);
	}

	/**
	 * 添加或覆盖关系定义，并把所属对象绑定为公式目标。
	 *
	 * @param relation 待添加的关系定义。
	 */
	public addRelation(relation: SketchRelationDefinition<TOwner>): void {
		relation.bindTarget(this.owner);
		this.relationMap.set(relation.id, relation);
	}

	/**
	 * 删除指定关系。
	 *
	 * @param relationId 当前会话内的关系标识。
	 * @returns 是否删除成功。
	 */
	public removeRelation(relationId: number): boolean {
		return this.relationMap.delete(relationId);
	}

	/** 清空容器内全部关系定义。 */
	public clearRelations(): void {
		this.relationMap.clear();
	}

	/**
	 * 获取指定关系。
	 *
	 * @param relationId 当前会话内的关系标识。
	 * @returns 匹配的关系定义；不存在时返回 `undefined`。
	 */
	public getRelation(relationId: number): SketchRelationDefinition<TOwner> | undefined {
		return this.relationMap.get(relationId);
	}

	/** @returns 当前全部关系定义的数组副本。 */
	public getRelations(): SketchRelationDefinition<TOwner>[] {
		return [...this.relationMap.values()];
	}

	/**
	 * 替换一个领域来源投影到当前对象的只读关系视图。
	 *
	 * @param sourceId 派生领域来源 ID，例如 `sketch-dimensions`。
	 * @param snapshots 该来源当前完整视图集合；空数组表示清除。
	 */
	public setDerivedRelationSnapshots(
		sourceId: string,
		snapshots: readonly SketchRelationSnapshot[],
	): void {
		if (snapshots.length === 0) {
			this.derivedRelationSnapshotsBySource.delete(sourceId);
			return;
		}

		/** 保存数组与条目的副本，禁止 UI 修改尺寸仓库的派生投影。 */
		this.derivedRelationSnapshotsBySource.set(
			sourceId,
			snapshots.map((snapshot) => ({...snapshot, isDerived: true, isReadOnly: true})),
		);
	}

	/**
	 * 查询派生只读关系视图。
	 *
	 * @param relationId 派生视图运行时 ID。
	 * @returns 匹配视图；不存在时返回 `undefined`。
	 */
	public getDerivedRelationSnapshot(relationId: number): SketchRelationSnapshot | undefined {
		for (const snapshots of this.derivedRelationSnapshotsBySource.values()) {
			const snapshot = snapshots.find((candidate) => candidate.id === relationId);

			if (snapshot) {
				return snapshot;
			}
		}

		return undefined;
	}

	/** @returns 关系面板使用的只读快照数组。 */
	public getRelationSnapshots(): SketchRelationSnapshot[] {
		const relationSnapshots = this.getRelations().map((relation) => ({
			id: relation.id,
			name: relation.name,
			relationType: relation.relationType,
			baseDisplayName: relation.baseDisplayName,
			...(relation.isReadOnly ? {isReadOnly: true} : {}),
			...(relation.isSuppressed ? {isSuppressed: true} : {}),
			...(!relation.isFailed && relation.isDangling
				? {
					isDangling: true,
					diagnosticMessage: relation.danglingDiagnosticMessage,
				}
				: {}),
			...(relation.isFailed
				? {
					isFailed: true,
					diagnosticMessage: relation.failureDiagnosticMessage,
				}
				: {}),
		}));
		const derivedSnapshots = [...this.derivedRelationSnapshotsBySource.values()]
			.flatMap((snapshots) => snapshots.map((snapshot) => ({...snapshot})));

		return [...relationSnapshots, ...derivedSnapshots];
	}
}
